import { useEffect, useMemo, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { CanvasTexture, DoubleSide, MeshBasicMaterial } from 'three'
import { config, type BandId } from '../config'
import { loadBandStripTexture, makePlaceholderStrip } from './filmFrameTextures'

/** 透明浅绿色 — 胶片带上下边框共用一个 material 实例 */
const edgeMaterial = new MeshBasicMaterial({
  color: 0x8ce6b4,
  transparent: true,
  opacity: 0.45,
  depthWrite: false,
})

type FilmBandProps = {
  band: BandId
  y: number
  scrollSpeed: number
  scrollDirection: 1 | -1
  blurPx: number
}

export function FilmBand({ band, y, scrollSpeed, scrollDirection, blurPx }: FilmBandProps) {
  const stripTexRef = useRef<CanvasTexture | null>(null)

  const [stripTex, setStripTex] = useState<CanvasTexture>(() => {
    const t = makePlaceholderStrip(band)
    t.repeat.x = config.filmstrip.visibleFramesPerArc
    stripTexRef.current = t
    return t
  })

  const material = useMemo(
    () =>
      new MeshBasicMaterial({
        side: DoubleSide,
        transparent: true,
        depthTest: true,
        depthWrite: false,
        toneMapped: false,
      }),
    [],
  )

  useEffect(() => {
    material.map = stripTex
    material.needsUpdate = true
  }, [material, stripTex])

  useEffect(() => {
    let alive = true
    loadBandStripTexture(band, blurPx).then((t) => {
      if (!alive) return
      t.repeat.x = config.filmstrip.visibleFramesPerArc
      stripTexRef.current = t
      setStripTex(t)
    })
    return () => {
      alive = false
    }
  }, [band, blurPx])

  useFrame((_, dt) => {
    const t = stripTexRef.current
    if (t) t.offset.x += scrollSpeed * scrollDirection * dt
  })

  const r = config.filmstrip.radius
  const h = config.filmstrip.bandHeight
  const thetaStart = config.filmstrip.thetaStart
  const thetaLength = config.filmstrip.thetaLength
  const edgeH = 0.025   // 边线厚度（米）
  const edgeR = r + 0.002 // 略微凸出，避免 z-fighting

  return (
    <group position={[0, y, 0]}>
      {/* 胶片主体 */}
      <mesh renderOrder={0} material={material}>
        <cylinderGeometry args={[r, r, h, 96, 1, true, thetaStart, thetaLength]} />
      </mesh>
      {/* 上边框 */}
      <mesh position={[0, h / 2, 0]} renderOrder={1} material={edgeMaterial}>
        <cylinderGeometry args={[edgeR, edgeR, edgeH, 96, 1, true, thetaStart, thetaLength]} />
      </mesh>
      {/* 下边框 */}
      <mesh position={[0, -h / 2, 0]} renderOrder={1} material={edgeMaterial}>
        <cylinderGeometry args={[edgeR, edgeR, edgeH, 96, 1, true, thetaStart, thetaLength]} />
      </mesh>
    </group>
  )
}
